import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUsers } from '../hooks/useFirestore';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, collection, addDoc, serverTimestamp, query, orderBy, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { generateAndDownloadVIPToken } from '../services/vipTokenGenerator';
import { COLLECTIONS } from '../utils/constants';
import { updatePublicStats } from '../services/firestoreService';
import '../styles/admin-responsive.css';

const ModernAdminDashboard = () => {
  const { currentUser, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: users } = useUsers();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [settings, setSettings] = useState({
    tokenPrice: 0,
    minTokensForDraw: 50,
    armyFundPercentage: 10
  });
  const [saveMessage, setSaveMessage] = useState('');
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetailModal, setShowUserDetailModal] = useState(false);
  const [detailUser, setDetailUser] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [showPrizeDrawModal, setShowPrizeDrawModal] = useState(false);
  const [prizeDrawData, setPrizeDrawData] = useState({
    title: '',
    prizeAmount: 10000,
    drawDate: '',
    description: '',
    armyFundPercentage: 10
  });
  const [prizeDraws, setPrizeDraws] = useState([]);
  const [winners, setWinners] = useState([]);
  const [showDeclareWinnerModal, setShowDeclareWinnerModal] = useState(false);
  const [selectedPrizeDraw, setSelectedPrizeDraw] = useState(null);
  const [editingDraw, setEditingDraw] = useState(null);
  const [selectedWinnerUser, setSelectedWinnerUser] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [donations, setDonations] = useState([]);
  const [processingUserId, setProcessingUserId] = useState(null);
  const [winnerBanner, setWinnerBanner] = useState(null);

  const stats = {
    totalUsers: users?.length || 0,
    tokensSold: users?.reduce((sum, user) => sum + (user.totalTokens || 0), 0) || 0,
    totalRevenue: users?.reduce((sum, user) => sum + ((user.totalTokens || 0) * 500), 0) || 0,
    totalWinners: winners?.length || 0,
    pendingApprovals: users?.filter(u => u.approvalStatus === 'pending').length || 0,
    approvedUsers: users?.filter(u => u.approvalStatus === 'approved').length || 0,
    armyFunds: users?.reduce((sum, user) => sum + (user.armyFundDonation || 0), 0) || 0,
  };

  const handleGenerateToken = (user) => {
    setSelectedUser(user);
    setShowTokenModal(true);
  };

  const handleViewUserDetails = (user) => {
    setDetailUser(user);
    setShowUserDetailModal(true);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const filteredUsers = users && Array.isArray(users) ? users.filter(user => {
    // Task 9: Exclude admin users from the list
    if (user.role === 'admin' || user.isAdmin === true) return false;
    
    const matchesSearch = user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         user.phone?.includes(searchQuery);
    
    const matchesStatus = statusFilter === 'all' || user.approvalStatus === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) : [];

  const formatCurrency = (amount) => {
    // eslint-disable-next-line no-undef
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Load Prize Draws from Firestore in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, COLLECTIONS.PRIZE_DRAWS), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const draws = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPrizeDraws(draws);
      },
      (error) => {
        console.error('Error loading prize draws:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Load Winners from Firestore in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, COLLECTIONS.WINNERS), orderBy('declaredAt', 'desc')),
      (snapshot) => {
        const winnersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setWinners(winnersData);
      },
      (error) => {
        console.error('Error loading winners:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Load Inquiries from Firestore in real-time
  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, COLLECTIONS.INQUIRIES), orderBy('createdAt', 'desc')),
      (snapshot) => {
        const inquiriesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setInquiries(inquiriesData);
      },
      (error) => {
        console.error('Error loading inquiries:', error);
      }
    );
    return () => unsubscribe();
  }, []);

  // Load Army Fund Donations from users
  useEffect(() => {
    if (users) {
      const donationsList = users
        .filter(user => user.armyFundDonation && user.armyFundDonation > 0)
        .map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          profileImage: user.profileImage,
          amount: user.armyFundDonation,
          isWinner: user.isWinner || false,
          donatedAt: user.armyFundDonatedAt || user.createdAt
        }))
        .sort((a, b) => {
          const dateA = a.donatedAt?.toDate ? a.donatedAt.toDate() : new Date(a.donatedAt);
          const dateB = b.donatedAt?.toDate ? b.donatedAt.toDate() : new Date(b.donatedAt);
          return dateB - dateA;
        });
      setDonations(donationsList);
    }
  }, [users]);

  useEffect(() => {
    if (!users || users.length === 0) return;

    const totalTokensSold = users.reduce((sum, user) => sum + (user.totalTokens || 0), 0);
    
    // Calculate total winning price from winners collection
    const totalWinningPrice = winners.reduce((sum, winner) => sum + (winner.finalAmount || 0), 0);
    
    // Calculate total army fund from winners collection
    const totalArmyFundFromWinners = winners.reduce((sum, winner) => sum + (winner.armyFundAmount || 0), 0);
    
    // Also include army fund donations from users
    const armyFundFromUsers = users.reduce((sum, user) => sum + (user.armyFundDonation || 0), 0);
    
    // Total army fund is sum of both
    const totalArmyFund = totalArmyFundFromWinners + armyFundFromUsers;
    
    const approvedUsersCount = users.filter(user => user.approvalStatus === 'approved').length;

    updatePublicStats({
      totalRegistrations: users.length,
      totalTokensSold,
      totalRevenue: totalWinningPrice, // Total winning price distributed
      totalWinners: winners.length, // Count from winners collection
      armyFundDonation: totalArmyFund,
      approvedUsers: approvedUsersCount
    });
  }, [users, winners]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside className={`${showMobileMenu ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-72 flex-col border-r border-gray-200 bg-white transition-transform duration-300 ease-in-out`}>
        <div className="flex h-full flex-col justify-between p-4">
          <div className="flex flex-col gap-4">
            {/* Brand */}
            <div className="flex gap-3 items-center px-2 py-3 border-b border-gray-100">
              <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-xl size-12 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                OL
              </div>
              <div className="flex flex-col">
                <h1 className="text-gray-900 text-base font-bold leading-tight">Admin Panel</h1>
                <p className="text-gray-500 text-xs font-medium">Super Admin</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex flex-col gap-1">
              <button
                onClick={() => {
                  setActiveSection('dashboard');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'dashboard'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>dashboard</span>
                <span className="text-sm">Dashboard</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('users');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'users'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>group</span>
                <span className="text-sm">Users</span>
                {stats.pendingApprovals > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {stats.pendingApprovals}
                  </span>
                )}
              </button>

              <button
                onClick={() => {
                  setActiveSection('tokens');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'tokens'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>confirmation_number</span>
                <span className="text-sm">VIP Tokens</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('draws');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'draws'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>card_giftcard</span>
                <span className="text-sm">Prize Draws</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('winners');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'winners'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>emoji_events</span>
                <span className="text-sm">Winners</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('payments');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'payments'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>payments</span>
                <span className="text-sm">Payments</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('inquiries');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'inquiries'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>mail</span>
                <span className="text-sm">Inquiries</span>
              </button>

              <button
                onClick={() => {
                  setActiveSection('armyfunds');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  activeSection === 'armyfunds'
                    ? 'bg-purple-50 text-purple-700 border-l-4 border-purple-700 font-bold'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>volunteer_activism</span>
                <span className="text-sm">Army Funds</span>
              </button>

              <button
                onClick={() => {
                  navigate('/admin/products');
                  setShowMobileMenu(false);
                }}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>inventory_2</span>
                <span className="text-sm">Manage Products</span>
              </button>

              <button
                onClick={() => {
                  navigate('/admin/videos');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-gray-600 hover:bg-gray-50 hover:text-gray-900`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>video_library</span>
                <span className="text-sm">Manage Videos</span>
              </button>

              <button
                onClick={() => {
                  navigate('/admin/gallery');
                  setShowMobileMenu(false);
                }}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-gray-600 hover:bg-gray-50 hover:text-gray-900`}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>photo_library</span>
                <span className="text-sm">Manage Gallery</span>
              </button>
            </nav>
          </div>

          {/* Bottom Settings */}
          <div className="flex flex-col gap-2 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setActiveSection('settings');
                setShowMobileMenu(false);
              }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>settings</span>
              <span className="text-sm font-medium">Settings</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>logout</span>
              <span className="text-sm font-medium">Log Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex flex-1 flex-col h-full bg-gray-50 relative overflow-y-auto">
        {/* Top Header */}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white/95 backdrop-blur px-6 py-3 shadow-sm">
          <div className="flex items-center gap-4 lg:hidden">
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="text-gray-600 hover:text-gray-900"
            >
              <span className="material-symbols-outlined">menu</span>
            </button>
            <div className="text-lg font-bold text-gray-900">Offer Lifetime</div>
          </div>

          <div className="hidden lg:flex items-center gap-2 text-sm text-gray-500">
            <span>Admin</span>
            <span className="material-symbols-outlined text-[16px]">chevron_right</span>
            <span className="text-gray-900 font-medium capitalize">{activeSection}</span>
          </div>

          <div className="flex flex-1 justify-end gap-6">
            <div className="hidden md:flex relative max-w-[320px] w-full items-center">
              <span className="material-symbols-outlined absolute left-3 text-gray-400" style={{ fontSize: '20px' }}>search</span>
              <input
                className="w-full bg-gray-100 text-gray-900 text-sm rounded-lg border-none py-2 pl-10 pr-4 placeholder-gray-500 focus:ring-2 focus:ring-purple-500"
                placeholder="Global search..."
                type="text"
              />
            </div>

            <div className="flex gap-2 items-center">
              <button className="flex items-center justify-center rounded-lg size-9 hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors relative">
                <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>notifications</span>
                <span className="absolute top-2 right-2 size-2 bg-red-500 rounded-full border border-white"></span>
              </button>

              <div className="w-px h-6 bg-gray-200 mx-1"></div>

              <div className="flex items-center gap-2">
                <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-full size-8 flex items-center justify-center text-white font-bold text-sm shadow">
                  A
                </div>
                <span className="hidden md:block text-sm font-medium text-gray-900">Admin</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="flex flex-col p-6 gap-6 max-w-[1400px] w-full mx-auto">
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Dashboard Overview</h1>
                  <p className="text-gray-600 text-base">Monitor your platform's performance and key metrics.</p>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-gray-200 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <span className="material-symbols-outlined text-purple-600" style={{ fontSize: '80px' }}>group</span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">Total Users</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-gray-900 text-3xl font-bold">{stats.totalUsers}</p>
                    <span className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-1.5 py-0.5 rounded">
                      <span className="material-symbols-outlined text-[14px]">trending_up</span> 12%
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-gray-200 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <span className="material-symbols-outlined text-blue-600" style={{ fontSize: '80px' }}>confirmation_number</span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">Tokens Sold</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-gray-900 text-3xl font-bold">{stats.tokensSold}</p>
                    <span className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-1.5 py-0.5 rounded">
                      <span className="material-symbols-outlined text-[14px]">trending_up</span> 8%
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-gray-200 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <span className="material-symbols-outlined text-green-600" style={{ fontSize: '80px' }}>payments</span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">Total Revenue</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-gray-900 text-3xl font-bold">{formatCurrency(stats.totalRevenue)}</p>
                    <span className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-1.5 py-0.5 rounded">
                      <span className="material-symbols-outlined text-[14px]">trending_up</span> 15%
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-1 rounded-xl p-5 bg-white border border-gray-200 relative overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="absolute right-0 top-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                    <span className="material-symbols-outlined text-yellow-600" style={{ fontSize: '80px' }}>emoji_events</span>
                  </div>
                  <p className="text-gray-600 text-sm font-medium">Total Winners</p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-gray-900 text-3xl font-bold">{stats.totalWinners}</p>
                    <span className="flex items-center text-green-600 text-xs font-bold bg-green-50 px-1.5 py-0.5 rounded">
                      <span className="material-symbols-outlined text-[14px]">trending_up</span> 5%
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-gray-900 font-bold text-lg mb-4">Pending Approvals</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-4xl font-bold text-purple-600">{stats.pendingApprovals}</p>
                      <p className="text-sm text-gray-600 mt-1">Users waiting for approval</p>
                    </div>
                    <button
                      onClick={() => setActiveSection('users')}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm"
                    >
                      Review Now
                    </button>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-gray-900 font-bold text-lg mb-4">Approved Users</h3>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-4xl font-bold text-green-600">{stats.approvedUsers}</p>
                      <p className="text-sm text-gray-600 mt-1">Active platform users</p>
                    </div>
                    <button
                      onClick={() => setActiveSection('tokens')}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm"
                    >
                      Issue Tokens
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

      {/* User Detail Modal */}
      {showUserDetailModal && detailUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-[modalFade_0.3s_ease]">
            <div className="relative shrink-0">
              <div className="h-32 bg-gradient-to-r from-purple-600 to-purple-800"></div>
              <div className="absolute inset-x-0 top-20 flex flex-col items-center px-6">
                <div className="size-20 rounded-2xl border-4 border-white shadow-xl bg-purple-100 flex items-center justify-center text-3xl font-bold text-purple-700 uppercase">
                  {detailUser.profileImage ? (
                    <img src={detailUser.profileImage} alt={detailUser.name} className="w-full h-full object-cover rounded-2xl" />
                  ) : (
                    detailUser.name?.[0] || 'U'
                  )}
                </div>
                <h2 className="mt-4 text-2xl font-bold text-gray-900">{detailUser.name || 'Unknown User'}</h2>
                <p className="text-gray-500">{detailUser.email || 'Email not provided'}</p>
                <div className="mt-3 flex flex-wrap gap-2 justify-center">
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${detailUser.approvalStatus === 'approved' ? 'bg-emerald-100 text-emerald-700' : detailUser.approvalStatus === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                    {detailUser.approvalStatus || 'Status N/A'}
                  </span>
                  {detailUser.isBlocked && (
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-rose-100 text-rose-700">Blocked</span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-6 mt-20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[{
                  label: 'Phone', value: detailUser.phone || 'Not provided'
                }, {
                  label: 'Country', value: detailUser.country || 'Not provided'
                }, {
                  label: 'State', value: detailUser.state || 'Not provided'
                }, {
                  label: 'City', value: detailUser.city || 'Not provided'
                }, {
                  label: 'UPI Name', value: detailUser.upiName || 'Not provided'
                }, {
                  label: 'UPI Number', value: detailUser.upiNumber || 'Not provided'
                }, {
                  label: 'Instagram', value: detailUser.instagram || 'Not provided'
                }, {
                  label: 'Facebook', value: detailUser.facebook || 'Not provided'
                }, {
                  label: 'YouTube', value: detailUser.youtube || 'Not provided'
                }, {
                  label: 'Remarks', value: detailUser.remarks || 'Not provided'
                }, {
                  label: 'Tokens', value: detailUser.totalTokens ?? detailUser.vipTokenCount ?? 0
                }].map((item) => (
                  <div key={item.label} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                    <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">{item.label}</p>
                    <p className="mt-1 text-sm font-semibold text-gray-900 break-words">{item.value || 'Not provided'}</p>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Full Address</p>
                <p className="text-sm text-gray-900 leading-relaxed">{detailUser.address || 'Address not provided'}</p>
              </div>

              <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">Registered On</p>
                <p className="text-sm text-gray-900">{detailUser.createdAt?.toDate ? detailUser.createdAt.toDate().toLocaleString() : detailUser.createdAt || 'N/A'}</p>
              </div>
            </div>

            <div className="px-6 py-4 bg-white border-t border-gray-100 shadow-inner">
              <button
                className="w-full rounded-xl bg-gradient-to-r from-purple-600 to-purple-800 py-3 text-white font-semibold shadow-lg hover:shadow-xl transition"
                onClick={() => {
                  setShowUserDetailModal(false);
                  setDetailUser(null);
                }}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}

          {/* Users Section */}
          {activeSection === 'users' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">User Management</h1>
                  <p className="text-gray-600 text-base">Manage all registered users and their approval status.</p>
                </div>
                <div className="flex gap-3">
                  <button className="flex items-center gap-2 h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm font-bold transition-colors">
                    <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>download</span>
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
                <div className="w-full md:w-auto flex-1 max-w-lg">
                  <div className="relative w-full">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="material-symbols-outlined text-gray-400" style={{ fontSize: '20px' }}>search</span>
                    </div>
                    <input
                      className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg leading-5 bg-white text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 sm:text-sm"
                      placeholder="Search by name, email, or phone..."
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3 w-full md:w-auto">
                  <div className="relative">
                    <select
                      className="appearance-none bg-white border border-gray-200 text-gray-900 py-2.5 pl-4 pr-10 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 cursor-pointer min-w-[140px]"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>expand_more</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Data Table - Desktop */}
              <div className="flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto" style={{ display: 'none' }} data-desktop-table="true">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase font-bold tracking-wider">
                        <th className="p-4 w-12">
                          <input className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 size-4" type="checkbox" />
                        </th>
                        <th className="p-4 font-bold min-w-[240px]">User Details</th>
                        <th className="p-4 font-bold">Phone</th>
                        <th className="p-4 font-bold">Joined Date</th>
                        <th className="p-4 font-bold">Tokens</th>
                        <th className="p-4 font-bold">Status</th>
                        <th className="p-4 font-bold text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="group hover:bg-gray-50 transition-colors">
                          <td className="p-4 align-middle">
                            <input className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 size-4" type="checkbox" />
                          </td>
                          <td className="p-4 align-middle">
                            <div className="flex items-center gap-3">
                              <div className="size-10 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white font-bold shrink-0">
                                {user.name?.charAt(0) || 'U'}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-gray-900 font-bold text-sm">{user.name || 'N/A'}</span>
                                <span className="text-gray-500 text-xs">{user.email || 'N/A'}</span>
                              </div>
                            </div>
                          </td>
                          <td className="p-4 align-middle text-sm text-gray-900">{user.phone || 'N/A'}</td>
                          <td className="p-4 align-middle text-sm text-gray-600">{formatDate(user.createdAt)}</td>
                          <td className="p-4 align-middle text-sm text-gray-900 font-bold">{user.totalTokens || 0}</td>
                          <td className="p-4 align-middle">
                            {user.approvalStatus === 'approved' && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-green-50 text-green-700 border border-green-200">
                                <span className="size-1.5 rounded-full bg-green-600"></span>
                                Approved
                              </span>
                            )}
                            {user.approvalStatus === 'pending' && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-50 text-yellow-700 border border-yellow-200">
                                <span className="size-1.5 rounded-full bg-yellow-600"></span>
                                Pending
                              </span>
                            )}
                            {user.approvalStatus === 'rejected' && (
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-50 text-red-700 border border-red-200">
                                <span className="size-1.5 rounded-full bg-red-600"></span>
                                Rejected
                              </span>
                            )}
                          </td>
                          <td className="p-4 align-middle text-right">
                            <div className="flex gap-2 justify-end flex-wrap">
                              {user.approvalStatus === 'pending' && (
                                <>
                                  <button
                                    onClick={async () => {
                                      try {
                                        setProcessingUserId(user.id);
                                        await updateDoc(doc(db, 'users', user.id), { 
                                          approvalStatus: 'approved',
                                          approvedAt: serverTimestamp(),
                                          approvedBy: currentUser.email
                                        });
                                        alert(`✅ ${user.name} has been approved successfully!`);
                                      } catch (error) {
                                        console.error('Error approving user:', error);
                                        alert(`❌ Error approving user: ${error.message}`);
                                      } finally {
                                        setProcessingUserId(null);
                                      }
                                    }}
                                    disabled={processingUserId === user.id}
                                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {processingUserId === user.id ? 'Processing...' : 'Approve'}
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (confirm(`Are you sure you want to reject ${user.name}?`)) {
                                        try {
                                          setProcessingUserId(user.id);
                                          await updateDoc(doc(db, 'users', user.id), { 
                                            approvalStatus: 'rejected',
                                            rejectedAt: serverTimestamp()
                                          });
                                          alert(`${user.name} has been rejected.`);
                                        } catch (error) {
                                          console.error('Error rejecting user:', error);
                                          alert(`❌ Error rejecting user: ${error.message}`);
                                        } finally {
                                          setProcessingUserId(null);
                                        }
                                      }
                                    }}
                                    disabled={processingUserId === user.id}
                                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {processingUserId === user.id ? 'Processing...' : 'Reject'}
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleViewUserDetails(user)}
                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-bold"
                                title="View Details"
                              >
                                View
                              </button>
                              <button
                                onClick={async () => {
                                  const newBlockStatus = !user.isBlocked;
                                  if (confirm(`Are you sure you want to ${newBlockStatus ? 'block' : 'unblock'} ${user.name}?`)) {
                                    await updateDoc(doc(db, 'users', user.id), { 
                                      isBlocked: newBlockStatus,
                                      blockedAt: newBlockStatus ? serverTimestamp() : null
                                    });
                                  }
                                }}
                                className={`px-3 py-1.5 ${user.isBlocked ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg transition-colors text-xs font-bold`}
                                title={user.isBlocked ? 'Unblock User' : 'Block User'}
                              >
                                {user.isBlocked ? 'Unblock' : 'Block'}
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm(`Are you sure you want to DELETE ${user.name}? This action cannot be undone!`)) {
                                    if (confirm('Final confirmation: Delete this user permanently?')) {
                                      await deleteDoc(doc(db, 'users', user.id));
                                    }
                                  }
                                }}
                                className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs font-bold"
                                title="Delete User"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredUsers.length === 0 && (
                        <tr>
                          <td colSpan="7" className="p-8 text-center text-gray-500">
                            No users found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="p-4 space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-mobile-cards="true">
                  {filteredUsers.map((user) => (
                    <div key={user.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                        <div className="size-12 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white font-bold text-lg shrink-0">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="text-gray-900 font-bold text-base">{user.name || 'N/A'}</div>
                          <div className="text-gray-500 text-sm">{user.email || 'N/A'}</div>
                        </div>
                        {user.approvalStatus === 'approved' && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">Approved</span>
                        )}
                        {user.approvalStatus === 'pending' && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800">Pending</span>
                        )}
                        {user.approvalStatus === 'rejected' && (
                          <span className="px-2 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">Rejected</span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Phone</div>
                          <div className="text-sm text-gray-900">{user.phone || 'N/A'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Tokens</div>
                          <div className="text-sm text-gray-900 font-bold">{user.totalTokens || 0}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Joined</div>
                          <div className="text-sm text-gray-900">{formatDate(user.createdAt)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Status</div>
                          <div className="text-sm text-gray-900">{user.isBlocked ? '🚫 Blocked' : '✅ Active'}</div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {user.approvalStatus === 'pending' && (
                          <>
                            <button
                              onClick={async () => {
                                try {
                                  setProcessingUserId(user.id);
                                  await updateDoc(doc(db, 'users', user.id), { 
                                    approvalStatus: 'approved',
                                    approvedAt: serverTimestamp(),
                                    approvedBy: currentUser.email
                                  });
                                  alert(`✅ ${user.name} has been approved successfully!`);
                                } catch (error) {
                                  console.error('Error approving user:', error);
                                  alert(`❌ Error approving user: ${error.message}`);
                                } finally {
                                  setProcessingUserId(null);
                                }
                              }}
                              disabled={processingUserId === user.id}
                              className="flex-1 min-w-[calc(50%-0.25rem)] px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Approve'}
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm(`Are you sure you want to reject ${user.name}?`)) {
                                  try {
                                    setProcessingUserId(user.id);
                                    await updateDoc(doc(db, 'users', user.id), { 
                                      approvalStatus: 'rejected',
                                      rejectedAt: serverTimestamp()
                                    });
                                    alert(`${user.name} has been rejected.`);
                                  } catch (error) {
                                    console.error('Error rejecting user:', error);
                                    alert(`❌ Error rejecting user: ${error.message}`);
                                  } finally {
                                    setProcessingUserId(null);
                                  }
                                }
                              }}
                              disabled={processingUserId === user.id}
                              className="flex-1 min-w-[calc(50%-0.25rem)] px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {processingUserId === user.id ? 'Processing...' : 'Reject'}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleViewUserDetails(user)}
                          className="flex-1 min-w-[calc(50%-0.25rem)] px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-bold"
                        >
                          View
                        </button>
                        <button
                          onClick={async () => {
                            const newBlockStatus = !user.isBlocked;
                            if (confirm(`${newBlockStatus ? 'Block' : 'Unblock'} ${user.name}?`)) {
                              await updateDoc(doc(db, 'users', user.id), { 
                                isBlocked: newBlockStatus,
                                blockedAt: newBlockStatus ? serverTimestamp() : null
                              });
                            }
                          }}
                          className={`flex-1 min-w-[calc(50%-0.25rem)] px-3 py-2 ${user.isBlocked ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-600 hover:bg-orange-700'} text-white rounded-lg text-xs font-bold`}
                        >
                          {user.isBlocked ? 'Unblock' : 'Block'}
                        </button>
                        <button
                          onClick={async () => {
                            if (confirm(`DELETE ${user.name}? This cannot be undone!`)) {
                              if (confirm('Final confirmation?')) {
                                await deleteDoc(doc(db, 'users', user.id));
                              }
                            }
                          }}
                          className="flex-1 min-w-[calc(50%-0.25rem)] px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-bold"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                  {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No users found</div>
                  )}
                </div>

                {/* Pagination */}
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-sm">Rows per page:</span>
                    <select className="bg-white border border-gray-200 text-gray-900 text-sm rounded focus:ring-2 focus:ring-purple-500 py-1 pl-2 pr-6">
                      <option>10</option>
                      <option>20</option>
                      <option>50</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-gray-600 text-sm">1-{filteredUsers.length} of {stats.totalUsers}</span>
                    <div className="flex gap-1">
                      <button className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50">
                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>chevron_left</span>
                      </button>
                      <button className="p-1 rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900">
                        <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>chevron_right</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* VIP Tokens Section */}
          {activeSection === 'tokens' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">VIP Tokens Management</h1>
                  <p className="text-gray-600 text-base">Issue and manage VIP tokens for approved users.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Total Tokens Issued</p>
                  <p className="text-gray-900 text-3xl font-bold">{stats.tokensSold}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Approved Users</p>
                  <p className="text-gray-900 text-3xl font-bold">{stats.approvedUsers}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Pending Approvals</p>
                  <p className="text-gray-900 text-3xl font-bold">{stats.pendingApprovals}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-gray-900 font-bold text-lg mb-4">Approved Users - Ready for Token Generation</h3>
                
                {/* Desktop Table View */}
                <div className="overflow-x-auto" style={{ display: 'none' }} data-desktop-table="true">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase font-bold">
                        <th className="p-4">User</th>
                        <th className="p-4">Email</th>
                        <th className="p-4">Phone</th>
                        <th className="p-4">Tokens</th>
                        <th className="p-4">Approved Date</th>
                        <th className="p-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved' && (u.role !== 'admin' && !u.isAdmin)).map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <div className="size-8 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white font-bold text-xs">
                                {user.name?.charAt(0) || 'U'}
                              </div>
                              <span className="text-gray-900 font-medium text-sm">{user.name}</span>
                            </div>
                          </td>
                          <td className="p-4 text-sm text-gray-600">{user.email}</td>
                          <td className="p-4 text-sm text-gray-600">{user.phone}</td>
                          <td className="p-4 text-sm text-gray-900 font-bold">{user.totalTokens || 0}</td>
                          <td className="p-4 text-sm text-gray-600">{formatDate(user.approvedAt)}</td>
                          <td className="p-4 text-right">
                            <button 
                              onClick={() => handleGenerateToken(user)}
                              className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-bold transition-colors"
                            >
                              Generate Token
                            </button>
                          </td>
                        </tr>
                      ))}
                      {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved' && (u.role !== 'admin' && !u.isAdmin)).length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-gray-500">No approved users yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="space-y-4" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }} data-mobile-cards="true">
                  {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved' && (u.role !== 'admin' && !u.isAdmin)).map((user) => (
                    <div key={user.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                        <div className="size-12 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white font-bold text-lg shrink-0">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-gray-900 font-bold text-base break-words">{user.name}</div>
                          <div className="text-gray-500 text-sm break-all">{user.email}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Phone</div>
                          <div className="text-sm text-gray-900 break-all">{user.phone}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Tokens</div>
                          <div className="text-sm text-gray-900 font-bold">{user.totalTokens || 0}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-xs text-gray-500 font-semibold uppercase mb-1">Approved Date</div>
                          <div className="text-sm text-gray-900">{formatDate(user.approvedAt)}</div>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleGenerateToken(user)}
                        className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-bold transition-colors"
                      >
                        Generate VIP Token
                      </button>
                    </div>
                  ))}
                  {users && Array.isArray(users) && users.filter(u => u.approvalStatus === 'approved' && (u.role !== 'admin' && !u.isAdmin)).length === 0 && (
                    <div className="text-center py-8 text-gray-500">No approved users yet</div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Prize Draws Section */}
          {activeSection === 'draws' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Prize Draws</h1>
                  <p className="text-gray-600 text-base">Manage prize draws and lottery events.</p>
                </div>
                <button 
                  onClick={() => setShowPrizeDrawModal(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>
                  Create New Draw
                </button>
              </div>

              {prizeDraws.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-gray-300 mb-4" style={{ fontSize: '64px' }}>card_giftcard</span>
                    <h3 className="text-gray-900 font-bold text-lg mb-2">No Prize Draws Yet</h3>
                    <p className="text-gray-600 mb-4">Create your first prize draw to get started</p>
                    <button 
                      onClick={() => setShowPrizeDrawModal(true)}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium text-sm"
                    >
                      Create Prize Draw
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {prizeDraws.map((draw) => (
                    <div key={draw.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{draw.title}</h3>
                          <p className="text-sm text-gray-600">{draw.description || 'No description'}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          draw.status === 'upcoming' ? 'bg-blue-100 text-blue-700' :
                          draw.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {draw.status}
                        </span>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="material-symbols-outlined text-purple-600" style={{ fontSize: '20px' }}>calendar_today</span>
                          <span className="text-gray-700">Draw Date:</span>
                          <span className="font-bold text-gray-900">{new Date(draw.drawDate).toLocaleDateString('en-IN')}</span>
                        </div>

                        <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-3">
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-700">Prize Amount:</span>
                              <span className="font-bold text-gray-900">{formatCurrency(draw.prizeAmount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-700">Army Fund ({draw.armyFundPercentage}%):</span>
                              <span className="font-bold text-red-600">- {formatCurrency(draw.prizeAmount * draw.armyFundPercentage / 100)}</span>
                            </div>
                            <div className="border-t border-purple-300 pt-2 flex justify-between">
                              <span className="text-gray-900 font-bold">Winner Gets:</span>
                              <span className="font-bold text-green-700">{formatCurrency(draw.prizeAmount - (draw.prizeAmount * draw.armyFundPercentage / 100))}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-xs font-bold transition-colors"
                          onClick={() => handleViewUserDetails(user)}
                        >
                          View Details
                        </button>
                        <button 
                          onClick={() => {
                            setEditingDraw(draw);
                            setPrizeDrawData({
                              title: draw.title,
                              prizeAmount: draw.prizeAmount,
                              drawDate: draw.drawDate,
                              description: draw.description,
                              armyFundPercentage: draw.armyFundPercentage
                            });
                            setShowPrizeDrawModal(true);
                          }}
                          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-bold transition-colors"
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>edit</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Winners Section */}
          {activeSection === 'winners' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Winners</h1>
                  <p className="text-gray-600 text-base">View and manage prize winners with distribution breakdown.</p>
                </div>
                <button 
                  onClick={() => setShowDeclareWinnerModal(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>add</span>
                  Declare Winner
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Total Winners</p>
                  <p className="text-gray-900 text-3xl font-bold">{stats.totalWinners}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Total Prize Money</p>
                  <p className="text-gray-900 text-3xl font-bold">{formatCurrency(stats.totalWinners * 10000)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Army Fund Collected</p>
                  <p className="text-gray-900 text-3xl font-bold">{formatCurrency(stats.armyFunds)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Net Distributed</p>
                  <p className="text-gray-900 text-3xl font-bold">{formatCurrency((stats.totalWinners * 10000) - stats.armyFunds)}</p>
                </div>
              </div>

              {/* Prize Distribution Calculator */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6">
                <h3 className="text-purple-900 font-bold text-lg mb-4 flex items-center gap-2">
                  <span className="material-symbols-outlined" style={{ fontSize: '24px' }}>calculate</span>
                  Prize Distribution Calculator
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <p className="text-gray-600 text-sm mb-2">Original Prize Amount</p>
                    <p className="text-gray-900 text-2xl font-bold">{formatCurrency(10000)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <p className="text-gray-600 text-sm mb-2">Army Fund Deduction ({settings.armyFundPercentage}%)</p>
                    <p className="text-red-600 text-2xl font-bold">- {formatCurrency(10000 * settings.armyFundPercentage / 100)}</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4">
                    <p className="text-green-100 text-sm mb-2">Final Prize to Winner</p>
                    <p className="text-white text-2xl font-bold">{formatCurrency(10000 - (10000 * settings.armyFundPercentage / 100))}</p>
                  </div>
                </div>
                <p className="text-purple-700 text-sm mt-4 italic">✓ This breakdown will be shown to winners before prize distribution</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-gray-900 font-bold text-lg mb-4">Winners List</h3>
                {winners.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase font-bold">
                          <th className="p-4">Winner</th>
                          <th className="p-4">Prize Draw</th>
                          <th className="p-4">Original Prize</th>
                          <th className="p-4">Army Fund</th>
                          <th className="p-4">Final Amount</th>
                          <th className="p-4">Declared On</th>
                          <th className="p-4">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {winners.map((winner) => (
                            <tr key={winner.id} className="hover:bg-gray-50">
                              <td className="p-4">
                                <div className="flex flex-col gap-1">
                                  <div className="flex items-center gap-2">
                                    <div className="size-8 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold text-xs">
                                      {winner.userName?.charAt(0) || 'W'}
                                    </div>
                                    <span className="text-gray-900 font-medium text-sm">{winner.userName}</span>
                                  </div>
                                  <span className="text-xs text-gray-500 ml-10">{winner.userEmail}</span>
                                </div>
                              </td>
                              <td className="p-4 text-sm text-gray-900 font-medium">{winner.prizeDrawTitle}</td>
                              <td className="p-4 text-sm text-gray-900 font-bold">{formatCurrency(winner.originalPrize)}</td>
                              <td className="p-4 text-sm text-red-600 font-bold">- {formatCurrency(winner.armyFundAmount)} ({winner.armyFundPercentage}%)</td>
                              <td className="p-4 text-sm text-green-600 font-bold">{formatCurrency(winner.finalAmount)}</td>
                              <td className="p-4 text-sm text-gray-600">{new Date(winner.declaredAt).toLocaleDateString('en-IN')}</td>
                              <td className="p-4">
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                                  {winner.status}
                                </span>
                              </td>
                            </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-gray-300 mb-4" style={{ fontSize: '64px' }}>emoji_events</span>
                    <h3 className="text-gray-900 font-bold text-lg mb-2">No Winners Yet</h3>
                    <p className="text-gray-600">Winners will appear here after prize draws</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Payments Section */}
          {activeSection === 'payments' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Payments & Transactions</h1>
                  <p className="text-gray-600 text-base">Monitor all payment transactions and revenue.</p>
                </div>
                <button className="flex items-center gap-2 h-10 px-4 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 text-sm font-bold">
                  <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>download</span>
                  Export Report
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex flex-col">
                    <p className="text-gray-600 text-xs font-medium mb-1">Tokens Sold</p>
                    <p className="text-gray-900 text-xl font-bold">{stats.tokensSold}</p>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex flex-col">
                    <p className="text-gray-600 text-xs font-medium mb-1">Avg. per User</p>
                    <p className="text-gray-900 text-xl font-bold">{formatCurrency(stats.totalUsers > 0 ? stats.totalRevenue / stats.totalUsers : 0)}</p>
                  </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <div className="flex flex-col">
                    <p className="text-gray-600 text-xs font-medium mb-1">Army Fund (10%)</p>
                    <p className="text-gray-900 text-xl font-bold">{formatCurrency(stats.totalRevenue * 0.1)}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-gray-900 font-bold text-lg mb-4">Recent Transactions</h3>
                <div className="text-center py-12">
                  <span className="material-symbols-outlined text-gray-300 mb-4" style={{ fontSize: '64px' }}>payments</span>
                  <h3 className="text-gray-900 font-bold text-lg mb-2">No Transactions Yet</h3>
                  <p className="text-gray-600">Payment transactions will appear here</p>
                </div>
              </div>
            </>
          )}

          {/* Inquiries Section */}
          {activeSection === 'inquiries' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Contact Inquiries</h1>
                  <p className="text-gray-600 text-base">Manage customer inquiries and support requests.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Total Inquiries</p>
                  <p className="text-gray-900 text-3xl font-bold">{inquiries.length}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">New/Pending</p>
                  <p className="text-gray-900 text-3xl font-bold">{inquiries.filter(i => i.status === 'new' || i.status === 'pending').length}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Resolved</p>
                  <p className="text-gray-900 text-3xl font-bold">{inquiries.filter(i => i.status === 'resolved').length}</p>
                </div>
              </div>

              {inquiries.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-gray-300 mb-4" style={{ fontSize: '64px' }}>mail</span>
                    <h3 className="text-gray-900 font-bold text-lg mb-2">No Inquiries Yet</h3>
                    <p className="text-gray-600">Customer inquiries will appear here</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subject</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {inquiries.map((inquiry) => (
                          <tr key={inquiry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(inquiry.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{inquiry.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{inquiry.email}</div>
                              <div className="text-sm text-gray-500">{inquiry.phone}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                {inquiry.subject}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {inquiry.message}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                inquiry.status === 'resolved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {inquiry.status || 'new'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => {
                                  const whatsappMessage = `Hello ${inquiry.name},\n\nThank you for contacting Offer Lifetime regarding: ${inquiry.subject}\n\nYour message: "${inquiry.message}"\n\nWe're here to help! How can we assist you?`;
                                  const whatsappUrl = `https://wa.me/${inquiry.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;
                                  window.open(whatsappUrl, '_blank');
                                }}
                                className="text-green-600 hover:text-green-900 mr-3"
                                title="Reply via WhatsApp"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>chat</span>
                              </button>
                              <button
                                onClick={async () => {
                                  if (confirm('Mark this inquiry as resolved?')) {
                                    await updateDoc(doc(db, COLLECTIONS.INQUIRIES, inquiry.id), { status: 'resolved' });
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title="Mark as Resolved"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>check_circle</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Army Funds Section */}
          {activeSection === 'armyfunds' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Army Funds Donations</h1>
                  <p className="text-gray-600 text-base">Track and manage army fund donations from winners.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-500 to-green-600 border border-green-200 rounded-xl p-5 text-white">
                  <p className="text-green-100 text-sm font-medium mb-2">Total Army Funds</p>
                  <p className="text-white text-3xl font-bold">{formatCurrency(stats.armyFunds)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Total Donors</p>
                  <p className="text-gray-900 text-3xl font-bold">{users?.filter(u => u.armyFundDonation > 0).length || 0}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">Average Donation</p>
                  <p className="text-gray-900 text-3xl font-bold">
                    {formatCurrency(stats.armyFunds / (users?.filter(u => u.armyFundDonation > 0).length || 1))}
                  </p>
                </div>
                <div className="bg-white border border-gray-200 rounded-xl p-5">
                  <p className="text-gray-600 text-sm font-medium mb-2">This Month</p>
                  <p className="text-gray-900 text-3xl font-bold">{formatCurrency(0)}</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-gray-900 font-bold text-lg mb-4">Donation History</h3>
                {donations.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-xs uppercase font-bold">
                          <th className="p-4">Donor Profile</th>
                          <th className="p-4">Contact</th>
                          <th className="p-4">Donation Amount</th>
                          <th className="p-4">Donation Date</th>
                          <th className="p-4">Type</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {donations.map((donation) => (
                          <tr key={donation.id} className="hover:bg-gray-50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                {donation.profileImage ? (
                                  <img 
                                    src={donation.profileImage} 
                                    alt={donation.name}
                                    className="size-10 rounded-full object-cover border-2 border-green-500"
                                  />
                                ) : (
                                  <div className="size-10 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-sm border-2 border-green-500">
                                    {donation.name?.charAt(0) || 'U'}
                                  </div>
                                )}
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-900 font-medium text-sm">{donation.name}</span>
                                    {donation.isWinner && (
                                      <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-yellow-100 text-yellow-800">
                                        🏆 Winner
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div className="text-sm text-gray-900">{donation.email}</div>
                            </td>
                            <td className="p-4 text-sm text-green-600 font-bold">{formatCurrency(donation.amount)}</td>
                            <td className="p-4 text-sm text-gray-600">{formatDate(donation.donatedAt)}</td>
                            <td className="p-4">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
                                {donation.isWinner ? 'Winner Donation' : 'User Donation'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <span className="material-symbols-outlined text-gray-300 mb-4" style={{ fontSize: '64px' }}>volunteer_activism</span>
                    <h3 className="text-gray-900 font-bold text-lg mb-2">No Donations Yet</h3>
                    <p className="text-gray-600">Army fund donations from winners will appear here</p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <>
              <div className="flex flex-wrap justify-between items-end gap-4">
                <div className="flex flex-col gap-1">
                  <h1 className="text-gray-900 text-3xl font-bold tracking-tight">Platform Settings</h1>
                  <p className="text-gray-600 text-base">Configure platform settings and preferences.</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-gray-900 font-bold text-lg mb-4">General Settings</h3>
                <div className="grid gap-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">VIP Token Price (₹)</label>
                    <input 
                      type="number" 
                      value={settings.tokenPrice} 
                      onChange={(e) => setSettings({...settings, tokenPrice: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Tokens for Draw</label>
                    <input 
                      type="number" 
                      value={settings.minTokensForDraw} 
                      onChange={(e) => setSettings({...settings, minTokensForDraw: parseInt(e.target.value)})}
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Army Fund Donation (%)</label>
                    <input 
                      type="number" 
                      value={settings.armyFundPercentage} 
                      onChange={(e) => setSettings({...settings, armyFundPercentage: parseInt(e.target.value)})}
                      min="0"
                      max="100"
                      className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" 
                    />
                    <p className="text-sm text-gray-500 mt-1">Percentage of winner's prize to be donated to Army Funds (0-100%)</p>
                  </div>

                  {/* Prize Calculation Preview */}
                  <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4 mt-2">
                    <h4 className="text-green-900 font-bold text-sm mb-3 flex items-center gap-2">
                      <span className="material-symbols-outlined" style={{ fontSize: '20px' }}>calculate</span>
                      Prize Distribution Preview
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Original Prize Amount:</span>
                        <span className="font-bold text-gray-900">{formatCurrency(10000)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Army Fund Deduction ({settings.armyFundPercentage}%):</span>
                        <span className="font-bold text-red-600">- {formatCurrency(10000 * settings.armyFundPercentage / 100)}</span>
                      </div>
                      <div className="border-t border-green-300 pt-2 flex justify-between">
                        <span className="text-gray-900 font-bold">Final Prize to Winner:</span>
                        <span className="font-bold text-green-700 text-lg">{formatCurrency(10000 - (10000 * settings.armyFundPercentage / 100))}</span>
                      </div>
                    </div>
                    <p className="text-xs text-green-700 mt-3 italic">✓ This is a preview based on a ₹10,000 prize</p>
                  </div>

                  {saveMessage && (
                    <div className={`p-3 rounded-lg ${saveMessage.includes('success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {saveMessage}
                    </div>
                  )}

                  <button 
                    onClick={() => {
                      setSaveMessage('Settings saved successfully!');
                      setTimeout(() => setSaveMessage(''), 3000);
                    }}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium w-fit transition-colors"
                  >
                    Save Settings
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Mobile menu overlay */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
        ></div>
      )}

      {/* Token Generation Modal */}
      {showTokenModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Generate VIP Token</h3>
              <button 
                onClick={() => setShowTokenModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="mb-6">
              <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg mb-4">
                <div className="size-12 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center text-white font-bold">
                  {selectedUser.name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="font-bold text-gray-900">{selectedUser.name}</p>
                  <p className="text-sm text-gray-600">{selectedUser.email}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Current Tokens:</span>
                  <span className="font-bold text-gray-900">{selectedUser.totalTokens || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Token Price:</span>
                  <span className="font-bold text-gray-900">{formatCurrency(0)}</span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>⚠️ Important:</strong> Each user can only have ONE VIP token. If this user already has a token, generation will be blocked.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowTokenModal(false);
                }}
                disabled={generatingToken}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setGeneratingToken(true);
                  try {
                    // Check if user already has a token
                    if (selectedUser.totalTokens && selectedUser.totalTokens > 0) {
                      alert(`⚠️ ${selectedUser.name} already has a VIP token. Each user can only have ONE token.`);
                      setShowTokenModal(false);
                      setGeneratingToken(false);
                      return;
                    }
                    
                    // Generate single token
                    const result = await generateAndDownloadVIPToken(
                      selectedUser,
                      currentUser.email,
                      { uploadToStorage: false, saveRecord: true }
                    );
                    
                    if (result.success) {
                      alert(`✅ Successfully generated VIP token for ${selectedUser.name}!`);
                      setShowTokenModal(false);
                    } else if (result.errorCode === 'TOKEN_ALREADY_EXISTS') {
                      alert(`⚠️ ${selectedUser.name} already has a VIP token. Each user can only have ONE token.`);
                      setShowTokenModal(false);
                    } else {
                      alert(`❌ Error: ${result.message || 'Failed to generate token'}`);
                    }
                  } catch (error) {
                    console.error('Token generation error:', error);
                    alert('❌ Failed to generate token. Please try again.');
                  } finally {
                    setGeneratingToken(false);
                  }
                }}
                disabled={generatingToken}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {generatingToken ? (
                  <>
                    <span className="material-symbols-outlined animate-spin" style={{ fontSize: '20px' }}>progress_activity</span>
                    Generating...
                  </>
                ) : (
                  'Generate Token'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prize Draw Creation Modal */}
      {showPrizeDrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Create Prize Draw</h3>
              <button 
                onClick={() => setShowPrizeDrawModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Draw Title
                </label>
                <input
                  type="text"
                  value={prizeDrawData.title}
                  onChange={(e) => setPrizeDrawData({...prizeDrawData, title: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="e.g., Monthly Prize Draw - January 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prize Amount (₹)
                </label>
                <input
                  type="number"
                  value={prizeDrawData.prizeAmount}
                  onChange={(e) => setPrizeDrawData({...prizeDrawData, prizeAmount: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="10000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Draw Date
                </label>
                <input
                  type="date"
                  value={prizeDrawData.drawDate}
                  onChange={(e) => setPrizeDrawData({...prizeDrawData, drawDate: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={prizeDrawData.description}
                  onChange={(e) => setPrizeDrawData({...prizeDrawData, description: e.target.value})}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter draw description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Army Fund Discount (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={prizeDrawData.armyFundPercentage}
                  onChange={(e) => setPrizeDrawData({...prizeDrawData, armyFundPercentage: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="10"
                />
                <p className="text-xs text-gray-500 mt-1">Percentage of prize to be deducted for Army Fund</p>
              </div>

              {/* Prize Distribution Preview */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                <h4 className="text-purple-900 font-bold text-sm mb-3">Prize Distribution Preview</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Original Prize:</span>
                    <span className="font-bold text-gray-900">{formatCurrency(prizeDrawData.prizeAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Army Fund ({prizeDrawData.armyFundPercentage}%):</span>
                    <span className="font-bold text-red-600">- {formatCurrency(prizeDrawData.prizeAmount * prizeDrawData.armyFundPercentage / 100)}</span>
                  </div>
                  <div className="border-t border-purple-300 pt-2 flex justify-between">
                    <span className="text-gray-900 font-bold">Winner Receives:</span>
                    <span className="font-bold text-green-700 text-lg">{formatCurrency(prizeDrawData.prizeAmount - (prizeDrawData.prizeAmount * prizeDrawData.armyFundPercentage / 100))}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowPrizeDrawModal(false);
                  setEditingDraw(null);
                  setPrizeDrawData({
                    title: '',
                    prizeAmount: 10000,
                    drawDate: '',
                    description: '',
                    armyFundPercentage: 10
                  });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!prizeDrawData.title || !prizeDrawData.drawDate) {
                    alert('Please fill in all required fields');
                    return;
                  }
                  
                  try {
                    if (editingDraw) {
                      // Update existing prize draw in Firestore
                      await updateDoc(doc(db, COLLECTIONS.PRIZE_DRAWS, editingDraw.id), prizeDrawData);
                      alert(`Prize Draw Updated Successfully!\n\nTitle: ${prizeDrawData.title}\nPrize: ${formatCurrency(prizeDrawData.prizeAmount)}\nArmy Fund: ${prizeDrawData.armyFundPercentage}%`);
                    } else {
                      // Create new prize draw in Firestore
                      await addDoc(collection(db, COLLECTIONS.PRIZE_DRAWS), {
                        ...prizeDrawData,
                        createdAt: serverTimestamp(),
                        status: 'upcoming'
                      });
                      alert(`Prize Draw Created Successfully!\n\nTitle: ${prizeDrawData.title}\nPrize: ${formatCurrency(prizeDrawData.prizeAmount)}\nArmy Fund: ${prizeDrawData.armyFundPercentage}%`);
                    }
                  } catch (error) {
                    console.error('Error saving prize draw:', error);
                    alert('Error saving prize draw. Please try again.');
                  }
                  
                  setShowPrizeDrawModal(false);
                  setEditingDraw(null);
                  setPrizeDrawData({
                    title: '',
                    prizeAmount: 10000,
                    drawDate: '',
                    description: '',
                    armyFundPercentage: 10
                  });
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                {editingDraw ? 'Update Draw' : 'Create Draw'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Declare Winner Modal */}
      {showDeclareWinnerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Declare Winner</h2>
              <button 
                onClick={() => {
                  setShowDeclareWinnerModal(false);
                  setSelectedPrizeDraw(null);
                  setSelectedWinnerUser(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Prize Draw *
                </label>
                <select
                  value={selectedPrizeDraw?.id || ''}
                  onChange={(e) => {
                    const draw = prizeDraws.find(d => d.id === e.target.value);
                    setSelectedPrizeDraw(draw || null);
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a prize draw...</option>
                  {prizeDraws.map(draw => (
                    <option key={draw.id} value={draw.id}>
                      {draw.title} - {formatCurrency(draw.prizeAmount)} ({new Date(draw.drawDate).toLocaleDateString('en-IN')})
                    </option>
                  ))}
                </select>
              </div>

              {selectedPrizeDraw && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                  <h4 className="text-purple-900 font-bold text-sm mb-3">Prize Distribution</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Original Prize:</span>
                      <span className="font-bold text-gray-900">{formatCurrency(selectedPrizeDraw.prizeAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Army Fund ({selectedPrizeDraw.armyFundPercentage}%):</span>
                      <span className="font-bold text-red-600">- {formatCurrency(selectedPrizeDraw.prizeAmount * selectedPrizeDraw.armyFundPercentage / 100)}</span>
                    </div>
                    <div className="border-t border-purple-300 pt-2 flex justify-between">
                      <span className="text-gray-900 font-bold">Winner Receives:</span>
                      <span className="font-bold text-green-700 text-lg">{formatCurrency(selectedPrizeDraw.prizeAmount - (selectedPrizeDraw.prizeAmount * selectedPrizeDraw.armyFundPercentage / 100))}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Winner (Approved Users with Tokens) *
                </label>
                <select
                  value={selectedWinnerUser || ''}
                  onChange={(e) => setSelectedWinnerUser(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a user...</option>
                  {users?.filter(u => u.approvalStatus === 'approved' && (u.totalTokens || 0) > 0 && (u.role !== 'admin' && !u.isAdmin)).map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.email} (Tokens: {user.totalTokens || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => {
                  setShowDeclareWinnerModal(false);
                  setSelectedPrizeDraw(null);
                  setSelectedWinnerUser(null);
                }}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!selectedPrizeDraw) {
                    alert('Please select a prize draw');
                    return;
                  }
                  
                  if (!selectedWinnerUser) {
                    alert('Please select a winner');
                    return;
                  }
                  
                  const winner = users?.find(u => u.id === selectedWinnerUser);
                  if (winner) {
                    const newWinner = {
                      userId: winner.id,
                      userName: winner.name,
                      userEmail: winner.email,
                      userPhone: winner.phone,
                      prizeDrawId: selectedPrizeDraw.id,
                      prizeDrawTitle: selectedPrizeDraw.title,
                      originalPrize: selectedPrizeDraw.prizeAmount,
                      armyFundPercentage: selectedPrizeDraw.armyFundPercentage,
                      armyFundAmount: selectedPrizeDraw.prizeAmount * selectedPrizeDraw.armyFundPercentage / 100,
                      finalAmount: selectedPrizeDraw.prizeAmount - (selectedPrizeDraw.prizeAmount * selectedPrizeDraw.armyFundPercentage / 100),
                      declaredAt: serverTimestamp(),
                      status: 'declared'
                    };
                    
                    // Save to Firestore
                    addDoc(collection(db, 'winners'), newWinner)
                      .then((docRef) => {
                        setWinners([...winners, { id: docRef.id, ...newWinner, declaredAt: new Date().toISOString() }]);
                        
                        // Send WhatsApp notification
                        const whatsappMessage = `🎉 Congratulations ${winner.name}!\n\nYou have won the prize draw: ${selectedPrizeDraw.title}\n\n💰 Prize Details:\nOriginal Prize: ${formatCurrency(selectedPrizeDraw.prizeAmount)}\nArmy Fund (${selectedPrizeDraw.armyFundPercentage}%): ${formatCurrency(newWinner.armyFundAmount)}\n✅ You Receive: ${formatCurrency(newWinner.finalAmount)}\n\nThank you for supporting OFFER Lifetime!`;
                        
                        const whatsappUrl = `https://wa.me/${winner.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(whatsappMessage)}`;
                        window.open(whatsappUrl, '_blank');
                        
                        // Show winner banner instead of alert
                        setWinnerBanner({
                          type: 'success',
                          winnerName: winner.name,
                          winnerEmail: winner.email,
                          winnerPhone: winner.phone,
                          finalAmount: newWinner.finalAmount,
                          originalPrize: selectedPrizeDraw.prizeAmount,
                          armyFundAmount: newWinner.armyFundAmount,
                          armyFundPercentage: selectedPrizeDraw.armyFundPercentage,
                          prizeTitle: selectedPrizeDraw.title
                        });
                        
                        // Auto-dismiss banner after 8 seconds
                        setTimeout(() => {
                          setWinnerBanner(null);
                        }, 8000);
                      })
                      .catch((error) => {
                        console.error('Error saving winner:', error);
                        setWinnerBanner({
                          type: 'error',
                          message: 'Failed to save winner. Please try again.'
                        });
                        
                        // Auto-dismiss error banner after 5 seconds
                        setTimeout(() => {
                          setWinnerBanner(null);
                        }, 5000);
                      });
                    
                    setShowDeclareWinnerModal(false);
                    setSelectedPrizeDraw(null);
                  }
                }}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
              >
                Declare Winner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Winner Declaration Banner */}
      {winnerBanner && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] animate-slide-down">
          <div className={`${
            winnerBanner.type === 'success' 
              ? 'bg-gradient-to-br from-green-500 via-emerald-600 to-teal-600' 
              : 'bg-gradient-to-r from-red-500 to-rose-600'
          } text-white rounded-3xl shadow-2xl max-w-4xl w-full mx-4`}>
            <div className="p-8">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {winnerBanner.type === 'success' ? (
                    <>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="bg-white/20 p-3 rounded-2xl">
                          <span className="material-symbols-outlined text-6xl">emoji_events</span>
                        </div>
                        <div>
                          <h3 className="text-3xl font-bold mb-1">Winner Declared Successfully!</h3>
                          <p className="text-white/90 text-lg">Prize distribution completed</p>
                        </div>
                      </div>
                      
                      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="text-white/70 text-sm font-semibold mb-3 uppercase tracking-wide">Winner Details</h4>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-2xl">person</span>
                                <div>
                                  <p className="text-xs text-white/60">Name</p>
                                  <p className="text-lg font-bold">{winnerBanner.winnerName}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-2xl">email</span>
                                <div>
                                  <p className="text-xs text-white/60">Email</p>
                                  <p className="text-sm font-medium">{winnerBanner.winnerEmail}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-2xl">phone</span>
                                <div>
                                  <p className="text-xs text-white/60">Phone</p>
                                  <p className="text-sm font-medium">{winnerBanner.winnerPhone}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="text-white/70 text-sm font-semibold mb-3 uppercase tracking-wide">Prize Breakdown</h4>
                            <div className="space-y-3">
                              <div className="flex items-center gap-3">
                                <span className="material-symbols-outlined text-2xl">redeem</span>
                                <div>
                                  <p className="text-xs text-white/60">Prize Draw</p>
                                  <p className="text-base font-bold">{winnerBanner.prizeTitle}</p>
                                </div>
                              </div>
                              <div className="bg-white/10 rounded-xl p-3 space-y-2">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-white/80">Original Prize</span>
                                  <span className="text-lg font-bold">{formatCurrency(winnerBanner.originalPrize)}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm text-white/80">Army Fund ({winnerBanner.armyFundPercentage}%)</span>
                                  <span className="text-base font-semibold text-red-200">- {formatCurrency(winnerBanner.armyFundAmount)}</span>
                                </div>
                                <div className="border-t border-white/20 pt-2 flex justify-between items-center">
                                  <span className="text-base font-semibold">Winner Receives</span>
                                  <span className="text-2xl font-bold text-yellow-300">{formatCurrency(winnerBanner.finalAmount)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3">
                        <span className="material-symbols-outlined text-2xl">check_circle</span>
                        <p className="text-base font-medium">WhatsApp notification has been sent to the winner</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="material-symbols-outlined text-3xl">error</span>
                        <h3 className="text-xl font-bold">Error</h3>
                      </div>
                      <p className="text-white/90 ml-1">{winnerBanner.message}</p>
                    </>
                  )}
                </div>
                <button
                  onClick={() => setWinnerBanner(null)}
                  className="ml-4 p-2 hover:bg-white/20 rounded-xl transition-colors flex-shrink-0"
                >
                  <span className="material-symbols-outlined text-3xl">close</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModernAdminDashboard;
